import { CSSProperties, forwardRef, ReactNode } from 'react';
import { useThemeContext, Elements } from '@utils/providers/theme-context.tsx';

export type TextSize = 'xs' | 'sm' | 'base' | 'lg' | 'xl' | '2xl' | '3xl';
export type TextWeight = 'regular' | 'medium' | 'semibold' | 'bold';
export type TextTone =
  | 'strong'
  | 'muted'
  | 'brand'
  | 'danger'
  | 'success'
  | 'inverse';
export type TextAs = 'p' | 'span' | 'div' | 'label' | 'h1' | 'h2' | 'h3' | 'h4';

const SIZE_MAP: Record<TextSize, { fontSize: string; lineHeight: string }> = {
  xs: { fontSize: '0.75rem', lineHeight: '1.125rem' },
  sm: { fontSize: '0.875rem', lineHeight: '1.313rem' },
  base: { fontSize: '1rem', lineHeight: '1.5rem' },
  lg: { fontSize: '1.125rem', lineHeight: '1.688rem' },
  xl: { fontSize: '1.25rem', lineHeight: '1.875rem' },
  '2xl': { fontSize: '1.5rem', lineHeight: '2rem' },
  '3xl': { fontSize: '1.875rem', lineHeight: '2.375rem' }
};

const WEIGHT_MAP: Record<TextWeight, number> = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700
};

const TONE_TOKEN_MAP: Record<TextTone, Elements> = {
  strong: 'normalText',
  muted: 'inactiveText',
  brand: 'activeText',
  danger: 'danger',
  success: 'success',
  inverse: 'inverseText'
};

export type TextProps = {
  as?: TextAs;
  size?: TextSize;
  weight?: TextWeight;
  tone?: TextTone;
  display?: boolean;
  truncate?: boolean | 'clamp2';
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  id?: string;
  htmlFor?: string;
  title?: string;
  onClick?: React.MouseEventHandler<HTMLElement>;
  'aria-label'?: string;
};

export const Text = forwardRef<HTMLElement, TextProps>(function Text(
  {
    as = 'span',
    size = 'base',
    weight = 'regular',
    tone = 'strong',
    display = false,
    truncate = false,
    className,
    style,
    children,
    ...rest
  },
  ref
) {
  const { getColour } = useThemeContext();
  const Tag = as as React.ElementType;

  const sizeStyle = SIZE_MAP[size];
  const fontWeight = WEIGHT_MAP[weight];
  const color = getColour(TONE_TOKEN_MAP[tone]);
  const fontFamily = display ? 'var(--font-display)' : undefined;

  const truncateStyle: CSSProperties =
    truncate === 'clamp2'
      ? {
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }
      : truncate
        ? { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }
        : {};

  return (
    <Tag
      ref={ref}
      className={className}
      style={{
        ...sizeStyle,
        fontWeight,
        color,
        fontFamily,
        ...truncateStyle,
        ...style
      }}
      {...rest}
    >
      {children}
    </Tag>
  );
});

export type HeadingLevel = 1 | 2 | 3 | 4;

export type HeadingProps = Omit<TextProps, 'as' | 'display'> & {
  level?: HeadingLevel;
};

const HEADING_DEFAULT_SIZE: Record<HeadingLevel, TextSize> = {
  1: '3xl',
  2: '2xl',
  3: 'xl',
  4: 'lg'
};

export function Heading({
  level = 2,
  size,
  weight = 'semibold',
  tone = 'strong',
  ...rest
}: HeadingProps) {
  const as = `h${level}` as TextAs;
  return (
    <Text
      as={as}
      display
      size={size ?? HEADING_DEFAULT_SIZE[level]}
      weight={weight}
      tone={tone}
      {...rest}
    />
  );
}
